import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Icon } from '@shared/ui';
import clsx from 'clsx';
import type { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SideBarButtonProps {
  icon: string;
  route: string;
  title: string;
  badge?: string;
  activeRoutePrefixes?: readonly string[];
  /**
   * Mark active from the outside when the caller resolves it — needed where routes nest and
   * the prefix rules below would light up more than one button.
   */
  isActiveOverride?: boolean;
}

const isPathActiveForPrefix = (pathname: string, prefix: string): boolean => {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
};

const SideBarButton: FC<SideBarButtonProps> = ({
  icon = '',
  route = '/',
  title = '',
  badge,
  activeRoutePrefixes = [],
  isActiveOverride,
}) => {
  const location = useLocation();

  let isActive = false;

  if (activeRoutePrefixes.some((prefix) => isPathActiveForPrefix(location.pathname, prefix))) {
    isActive = true;
  }

  // Check for exact match first
  else if (location.pathname === route) {
    isActive = true;
  } else if (location.pathname.startsWith(route + '/')) {
    // For routes that are prefixes of the current path, only activate if:
    // 1. The route is NOT /admin (Admin Home should only match exactly)
    // 2. The route doesn't conflict with a more specific route

    // Special case: /admin should only match exactly, not as a prefix
    if (route === '/admin') {
      isActive = false;
    }
    // Special case: /admin/profiles should NOT match /admin/prompts
    else if (route === '/admin/prompts' && location.pathname.startsWith('/admin/profiles')) {
      isActive = false;
    }
    // Special case: /admin/prompts should NOT match /admin/profiles
    else if (route === '/admin/profiles' && location.pathname.startsWith('/admin/prompts')) {
      isActive = false;
    } else {
      isActive = true;
    }
  }
  if (isActiveOverride !== undefined) {
    isActive = isActiveOverride;
  }

  return (
    <Tooltip placement="right" title={title}>
      <Link to={route} className="relative">
        <IconButton
          className={clsx('rounded-2xl w-12 h-12 transition-colors', {
            'bg-sage-400 text-black': isActive,
            'text-sage-100 hover:bg-white/10': !isActive,
          })}
        >
          <Icon>{icon}</Icon>
        </IconButton>
        {badge && (
          <span className="absolute -top-1 -right-1 bg-ocean-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </Link>
    </Tooltip>
  );
};

export default SideBarButton;
